<template>
    <div class="subscribe-form">
        <div class="head">
            <div class="subscribe-img-wrapper">
                <img class="subscribe-img"
                     width="92"
                     height="56"
                     :src="asset_cdn('assets/subscribe.svg')"
                     loading="lazy"
                     :alt="getTrans('subscribe.newsletterButtonText', 'Squeeze Me In')"
                     :title="getTrans('subscribe.newsletterButtonText', 'Squeeze Me In')">
            </div>
            <div class="subscribe-text-section">
                <span class="h2 subscribe-title">
                    <Translate translationKey="subscribe.newsletterTitle"
                               fallback="Get Daily Crypto Insights That Finally Click" />
                </span>
                <p class="subscribe-subtitle">
                    <Translate translationKey="subscribe.newsletterSubtitle"
                               fallback="Join 120,000+ Daily Squeeze subscribers and get our 5 min newsletter with the juicy insights, and tips to spice up your investment game daily." />
                </p>
            </div>
        </div>
        <div class="content">
            <div class="form-group">
                <input v-model="email"
                       type="email"
                       :placeholder="getTrans('subscribe.inputPlaceholder', 'Enter email here')"
                       :class="{ 'error-wrapper--active': errorMessage }"
                       class="input form-control email"
                       name="email"
                       required>
                <div v-if="errorMessage"
                     class="error-message">
                    {{ errorMessage }}
                </div>
            </div>
            <div>
                <div id="subscribe-form-proceed-button" />
                <BaseButton class="g-recaptcha"
                            @click="loadRecaptchaAndProceed">
                    <Translate translationKey="subscribe.newsletterButtonText"
                               fallback="Squeeze Me In" />
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import Translate from '@/js/helpers/Translate.vue';
    import { asset_cdn } from '../helpers/helpers';
    import { getTrans } from '../helpers/getTrans';
    import { subscriberSourceEnum } from '../enums/subscriberSourceEnum';
    import { useSubscribeForm } from './composables/useSubscribeForm';
    import BaseButton from '@/js/components/BaseButton.vue';

    const SUBSCRIBE_FORM_BUTTON_ID = 'subscribe-form-proceed-button';
    const {
        email,
        errorMessage,
        loadRecaptchaAndProceed,
    } = useSubscribeForm(subscriberSourceEnum.SOURCE_NEWSLETTER_FOOTER, [subscriberSourceEnum.SOURCE_NEWSLETTER, subscriberSourceEnum.SOURCE_NEWSLETTER_FOOTER], SUBSCRIBE_FORM_BUTTON_ID, true);
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/breakpoints" as breakpoints;

.input {
    width: 100%;
    height: 50px;
    padding: 6px 12px;
    border-radius: 10px;
    background: variables.$baseWhite;
    font-size: 16px;
    outline: none;
    transition: 0.2s ease-in-out;

    &:focus {
        background: variables.$baseWhite;
    }

    &::placeholder {
        color: variables.$gray800;
    }
}

.subscribe-form {
    max-width: 690px;
    margin: auto;

    .subscribe-title {
        display: block;
        margin-bottom: 8px;

        @media (min-width: breakpoints.$md-breakpoint) {
            white-space: nowrap;
        }
    }

    .subscribe-subtitle {
        margin-bottom: 16px;
        font-size: 18px;
        font-weight: 400;
        line-height: 25px;
    }

    .subscribe-img {
        display: block;
        max-width: unset;
        padding: unset;
        border: unset;
        border-radius: 8px;
        margin: 0;
    }

    .error-wrapper--active {
        border: 1px solid variables.$error600;
    }

    .error-message {
        margin-top: 2px;
        color: variables.$error600;
        font-size: 12px;
        line-height: 19px;
    }
}

.subscribe-form {
    .head {
        display: block;
        align-items: center;
        justify-content: center;
        text-align: left;

        @media (min-width: breakpoints.$sm-breakpoint) {
            display: flex;
        }

        .subscribe-img-wrapper {
            display: flex;
            justify-content: center;
            margin-bottom: 24px;

            @media (min-width: breakpoints.$md-breakpoint) {
                margin-bottom: 0;
            }

            @media (min-width: breakpoints.$sm-breakpoint) {
                margin-right: 16px;
            }
        }

        .subscribe-text-section {
            margin-left: 16px;
            text-align: center;
        }
    }

    .content {
        display: flex;
        margin: 24px 0;

        .form-group {
            flex: 1;
            margin-bottom: 16px;
            text-align: left;

            @media (min-width: breakpoints.$md-breakpoint) {
                margin-right: 16px;
            }
        }
    }

    @media (max-width: breakpoints.$md-breakpoint) {
        .head {
            text-align: center;
        }

        .content {
            display: block;
            margin-bottom: 15px;
            text-align: center;
        }
    }
}

.dark-theme {
    .subscribe-title {
        color: variables.$baseWhite;
        font-size: 32px;
        font-weight: 400;
        line-height: 40px;
    }

    .subscribe-subtitle {
        color: variables.$gray300;
    }

    .input {
        color: variables.$gray800;
    }
}
</style>
